/*
 * Notifications.tsx — S57: Notification Center
 * API: GET /api/v1/notifications, PUT /api/v1/notifications/read-all
 */
import { useState, useEffect } from 'react';
import { API_V1, authFetch, safeFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import ActionButton from '../components/ui/ActionButton';

interface Notification {
  id: number;
  notification_type: string;
  title: string;
  body: string | null;
  priority: string | null;
  status: string;
  created_at: string | null;
  read_at: string | null;
  exception_id: number | null;
  correlation_group_id: number | null;
}

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'UNREAD'>('ALL');

  const load = () => {
    setLoading(true);
    setError('');
    safeFetch(`${API_V1}/notifications?limit=100`, { notifications: [] })
      .then(d => { setNotifications(d.notifications || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const markAllRead = () => {
    authFetch(`${API_V1}/notifications/read-all`, { method: 'PUT' })
      .then(() => load())
      .catch(e => setError(e.message));
  };

  const markRead = (id: number) => {
    authFetch(`${API_V1}/notifications/${id}/read`, { method: 'PUT' })
      .then(() => load())
      .catch(e => setError(e.message));
  };

  if (loading) return <div><div style={{ padding: 32, color: 'var(--text-hint)' }}>Loading notifications...</div></div>;
  if (error) return (
    <div>
      <div style={{ color: 'var(--negative)', marginBottom: 8 }}>Error: {error}</div>
      <ActionButton label="Retry" onClick={load} variant="secondary" />
    </div>
  );

  const unreadCount = notifications.filter(n => n.status !== 'READ').length;
  const filtered = filter === 'UNREAD' ? notifications.filter(n => n.status !== 'READ') : notifications;

  const typeIcon = (t: string) => {
    if (t.includes('SLA')) return '\u23F0';
    if (t.includes('ESCALATION')) return '\u2191';
    if (t.includes('ASSIGNMENT') || t.includes('NEW')) return '\u2709';
    if (t.includes('THRESHOLD')) return '\u2696';
    if (t.includes('DIGEST')) return '\u2211';
    return '\u2022';
  };

  const sevColor = (s: string | null | undefined) => {
    const v = (s ?? '').toUpperCase();
    if (v === 'CRITICAL' || v === 'HIGH' || v === 'URGENT') return 'var(--negative)';
    if (v === 'MEDIUM' || v === 'WARNING' || v === 'NORMAL') return 'var(--warning)';
    return 'var(--text-secondary)';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>{unreadCount > 0 ? `${unreadCount} unread` : 'All read'}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton label={filter === 'ALL' ? 'Show Unread' : 'Show All'} onClick={() => setFilter(filter === 'ALL' ? 'UNREAD' : 'ALL')} variant="secondary" size="sm" />
          {unreadCount > 0 && <ActionButton label="Mark All Read" onClick={markAllRead} size="sm" />}
        </div>
      </div>
      <KpiGrid columns={2} items={[
        { label: 'Total', value: notifications.length },
        { label: 'Unread', value: unreadCount, color: unreadCount > 0 ? 'var(--negative)' : 'var(--positive)' },
      ]} />

      {filtered.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 24, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-hint)' }}>
            {filter === 'UNREAD' ? 'No unread notifications.' : 'No notifications yet.'}
          </p>
        </div>
      ) : (
        filtered.map(n => (
          <div key={n.id}
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8,
              padding: 16, marginBottom: 8, cursor: n.status !== 'READ' ? 'pointer' : 'default',
              borderLeft: n.status !== 'READ' ? `3px solid ${sevColor(n.priority)}` : '3px solid transparent',
              opacity: n.status === 'READ' ? 0.7 : 1,
            }}
            onClick={() => n.status !== 'READ' && markRead(n.id)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>{typeIcon(n.notification_type)}</span>
                <span style={{ fontSize: 13, fontWeight: n.status !== 'READ' ? 600 : 400, color: 'var(--navy-900)' }}>{n.title}</span>
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-hint)' }}>
                {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
              </span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 22 }}>{n.body ?? ''}</div>
            <div style={{ display: 'flex', gap: 12, marginLeft: 22, marginTop: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>{(n.notification_type ?? '').replace(/_/g, ' ')}</span>
              {n.exception_id && <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>Exception #{n.exception_id}</span>}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
